<template>
  <div class="dash-root">
    <!-- ═══ Hero Header ════════════════════════════════════════ -->
    <div class="dash-hero">
      <div class="dash-hero__bg"></div>
      <div class="dash-hero__inner">
        <div class="dash-hero__brand">
          <div class="dash-hero__icon">
            <i class="fas fa-briefcase"></i>
          </div>
          <div>
            <h1 class="dash-hero__title">Business Suite</h1>
            <p class="dash-hero__subtitle">Manage business partners &amp; communicate</p>
          </div>
        </div>
        <div class="dash-hero__actions">
          <transition name="pop">
            <span v-if="selectedPartners.length > 0" class="selection-pill">
              <i class="fas fa-check-circle"></i>
              {{ selectedPartners.length }} selected
            </span>
          </transition>
          <button class="btn btn--templates-manager" @click="openTemplateManager">
            <i class="fas fa-layer-group"></i>
            <span class="btn__text">Templates</span>
          </button>
          <button class="btn btn--soft" @click="fetchBusinessSuite" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            <span class="btn__text">Refresh</span>
          </button>
          <button
            class="btn btn--primary"
            @click="openBroadcastDialog"
            :disabled="selectedPartners.length === 0"
          >
            <i class="fas fa-bullhorn"></i>
            <span class="btn__text">Broadcast ({{ selectedPartners.length }})</span>
          </button>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card" v-for="stat in statsCards" :key="stat.key">
          <div class="stat-card__icon" :style="{ background: stat.bg, color: stat.color }">
            <i :class="stat.icon"></i>
          </div>
          <div class="stat-card__body">
            <span class="stat-card__val">{{ stat.value }}</span>
            <span class="stat-card__lbl">{{ stat.label }}</span>
          </div>
          <div class="stat-card__bar" :style="{ background: stat.color, width: stat.percent + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- ═══ Table ═════════════════════════════════════════ -->
    <div class="table-wrap">
      <div class="table-toolbar">
        <div class="search-group">
          <i class="fas fa-search search-group__icon"></i>
          <input v-model="searchQuery" type="text" class="search-input" placeholder="Search by name..." />
          <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button
          class="btn btn--select-all"
          @click="toggleSelectAll"
          :title="allFilteredSelected ? 'Deselect all' : 'Select all'"
        >
          <i :class="allFilteredSelected ? 'fas fa-check-square' : 'far fa-square'"></i>
          <span class="btn__text">{{ allFilteredSelected ? 'Deselect all' : 'Select all' }}</span>
        </button>
        <span class="table-meta">
          <i class="fas fa-users"></i>
          {{ filteredBusinessSuite.length }} business suite{{ filteredBusinessSuite.length === 1 ? '' : 's' }}
        </span>
      </div>

      <v-data-table
        v-model="selectedPartners"
        :headers="headers"
        :items="filteredBusinessSuite"
        :loading="loading"
        class="business-table elevation-0"
        item-key="id"
        show-select
        :items-per-page="10"
        :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
      >
        <template v-slot:item.type="{ item }">
          <span class="type-badge type-badge--business">
            <i class="fas fa-briefcase"></i>
            {{ item.type }}
          </span>
        </template>

        <template v-slot:item.phone_number="{ item }">
          <span class="phone-cell">
            <i class="fab fa-whatsapp" v-if="item.phone_number"></i>
            <span v-else class="phone-cell--missing"><i class="fas fa-minus"></i></span>
            {{ item.phone_number || '—' }}
          </span>
        </template>

        <template v-slot:item.total_affiliates="{ item }">
          <span class="affiliate-count">
            <i class="fas fa-user-friends"></i>
            {{ item.total_affiliates || 0 }}
          </span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span class="date-cell">{{ formatDate(item.created_at) }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="action-group">
            <button class="action-btn action-btn--view" title="View details" @click.stop="viewDetails(item.id)">
              <i class="fas fa-eye"></i>
            </button>
            <button class="action-btn action-btn--email" title="Send email" @click="openCommunication(item, 'email')">
              <i class="fas fa-envelope"></i>
            </button>
            <button class="action-btn action-btn--whatsapp" title="Send WhatsApp" @click="openCommunication(item, 'whatsapp')">
              <i class="fab fa-whatsapp"></i>
            </button>
            <button class="action-btn action-btn--webpush" title="Send WebPush" @click="openCommunication(item, 'webpush')">
              <i class="fas fa-bell"></i>
            </button>
            <button class="action-btn action-btn--chat" title="Chat" @click="openCommunication(item, 'chat')">
              <i class="fas fa-comment-dots"></i>
            </button>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="empty-table">
            <div class="empty-table__illustration"><i class="fas fa-briefcase"></i></div>
            <p>No business suite found</p>
            <small>Business partners will appear here</small>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ═══ Communication Modal ═══════════════════════════════ -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-card modal-card--comm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon" :class="'modal-card__icon--' + selectedChannel">
                <i :class="channelIcon(selectedChannel)"></i>
              </span>
              <div>
                <div class="modal-card__title">Send {{ channelLabel(selectedChannel) }}</div>
                <div class="modal-card__subtitle">To {{ selectedPartner ? selectedPartner.username : '' }}</div>
              </div>
            </div>
            <button class="modal-close" @click="closeModal"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-card__body modal-body--split">
            <div class="modal-form-col">
              <div class="channel-picker">
                <button
                  v-for="ch in channels"
                  :key="ch.value"
                  class="channel-chip"
                  :class="{ 'channel-chip--active': selectedChannel === ch.value }"
                  @click="selectedChannel = ch.value"
                >
                  <i :class="ch.icon"></i>
                  <span>{{ ch.text }}</span>
                </button>
              </div>

              <button class="btn-use-template" @click="openTemplatePickerDialog('individual')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <transition name="field-slide">
                <div v-if="selectedChannel === 'email'" class="form-group">
                  <label class="form-label">Subject</label>
                  <input v-model="emailSubject" type="text" class="form-input" placeholder="Enter subject..." />
                </div>
              </transition>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → partner name · {link} → chat link</span>
                </label>
                <textarea
                  v-model="messageText"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your message..."
                  @input="updatePreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': messageText.length > 900 }">
                    {{ messageText.length }}/1000
                  </div>
                  <button v-if="messageText.trim()" class="btn-save-tpl" @click="openSaveTemplate('individual')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Live Preview</span>
                  <span class="preview-channel">{{ channelLabel(selectedChannel) }}</span>
                </div>

                <div v-if="selectedChannel === 'whatsapp'" class="preview-whatsapp">
                  <div class="wa-phone">
                    <div class="wa-phone__header"><i class="fab fa-whatsapp"></i> WhatsApp</div>
                    <div class="wa-phone__chat">
                      <div class="wa-bubble">
                        <div class="wa-bubble__text">{{ previewMessage }}</div>
                        <div class="wa-bubble__meta">
                          <span class="wa-bubble__time">{{ currentTime }}</span>
                          <span class="wa-bubble__tick">✓✓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="selectedChannel === 'email'" class="preview-email">
                  <div class="email-frame">
                    <div class="email-frame__bar">
                      <span class="email-frame__dots">● ● ●</span>
                      <span class="email-frame__url">pettycashglobal.com</span>
                    </div>
                    <div class="email-frame__body">
                      <div class="email-header">
                        <div class="email-header__from">
                          <div class="email-avatar">PC</div>
                          <div>
                            <div class="email-header__name">PettyCash Manager</div>
                            <div class="email-header__addr">manager@pettycashglobal.com</div>
                          </div>
                        </div>
                        <div class="email-header__date">{{ currentDate }}</div>
                      </div>
                      <div class="email-subject">{{ emailSubject || '(no subject)' }}</div>
                      <div class="email-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>

                <div v-else-if="selectedChannel === 'webpush'" class="preview-webpush">
                  <div class="push-frame">
                    <div class="push-frame__header">
                      <div class="push-app">
                        <div class="push-app__icon"><i class="fas fa-handshake"></i></div>
                        <div class="push-app__name">PettyCash</div>
                      </div>
                      <div class="push-time">now</div>
                    </div>
                    <div class="push-content">
                      <div class="push-title">Message from Manager</div>
                      <div class="push-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>

                <div v-else-if="selectedChannel === 'chat'" class="preview-chat">
                  <div class="chat-preview-box">
                    <div class="chat-preview-header">
                      <i class="fas fa-comment-dots"></i>
                      <span>In‑App Chat</span>
                    </div>
                    <div class="chat-preview-body">
                      <div class="chat-bubble">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeModal">Cancel</button>
            <button
              class="btn btn--send"
              :class="'btn--send--' + selectedChannel"
              :disabled="!messageText.trim() || sending"
              @click="sendCommunication"
            >
              <i v-if="sending" class="fas fa-spinner fa-spin"></i>
              <i v-else :class="channelIcon(selectedChannel)"></i>
              {{ sending ? 'Sending…' : 'Send ' + channelLabel(selectedChannel) }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Broadcast Modal ═══════════════════════════════ -->
    <transition name="modal">
      <div v-if="broadcastDialog" class="modal-overlay" @click.self="closeBroadcastDialog">
        <div class="modal-card modal-card--broadcast">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--broadcast">
                <i class="fas fa-bullhorn"></i>
              </span>
              <div>
                <div class="modal-card__title">Broadcast Message</div>
                <div class="modal-card__subtitle">
                  {{ selectedPartners.length }} recipient{{ selectedPartners.length === 1 ? '' : 's' }} selected
                </div>
              </div>
            </div>
            <button class="modal-close" @click="closeBroadcastDialog"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-card__body modal-body--split">
            <div class="modal-form-col">
              <div class="recipient-bar">
                <span class="recipient-bar__label">To:</span>
                <div class="recipient-chips">
                  <span v-for="p in selectedPartners.slice(0, 5)" :key="p.id" class="recipient-chip">{{ p.username }}</span>
                  <span v-if="selectedPartners.length > 5" class="recipient-chip recipient-chip--more">+{{ selectedPartners.length - 5 }} more</span>
                </div>
              </div>

              <div class="channel-picker">
                <button
                  v-for="ch in broadcastChannels"
                  :key="ch.value"
                  class="channel-chip"
                  :class="{ 'channel-chip--active': broadcastChannel === ch.value }"
                  @click="broadcastChannel = ch.value"
                >
                  <i :class="ch.icon"></i>
                  <span>{{ ch.text }}</span>
                </button>
              </div>

              <button class="btn-use-template" @click="openTemplatePickerDialog('broadcast')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <transition name="field-slide">
                <div v-if="broadcastChannel === 'email'" class="form-group">
                  <label class="form-label">Subject</label>
                  <input v-model="broadcastSubject" type="text" class="form-input" placeholder="Enter subject..." />
                </div>
              </transition>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → partner name · {link} → chat link</span>
                </label>
                <textarea
                  v-model="broadcastMessage"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your broadcast message..."
                  @input="updateBroadcastPreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': broadcastMessage.length > 900 }">
                    {{ broadcastMessage.length }}/1000
                  </div>
                  <button v-if="broadcastMessage.trim()" class="btn-save-tpl" @click="openSaveTemplate('broadcast')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Preview (first recipient)</span>
                  <span class="preview-channel">{{ channelLabel(broadcastChannel) }}</span>
                </div>

                <div v-if="broadcastChannel === 'email'" class="preview-email">
                  <div class="email-frame">
                    <div class="email-frame__bar">
                      <span class="email-frame__dots">● ● ●</span>
                      <span class="email-frame__url">pettycashglobal.com</span>
                    </div>
                    <div class="email-frame__body">
                      <div class="email-header">
                        <div class="email-header__from">
                          <div class="email-avatar">PC</div>
                          <div>
                            <div class="email-header__name">PettyCash Manager</div>
                            <div class="email-header__addr">manager@pettycashglobal.com</div>
                          </div>
                        </div>
                        <div class="email-header__date">{{ currentDate }}</div>
                      </div>
                      <div class="email-subject">{{ broadcastSubject || '(no subject)' }}</div>
                      <div class="email-body">{{ broadcastPreview }}</div>
                    </div>
                  </div>
                </div>

                <div v-else-if="broadcastChannel === 'webpush'" class="preview-webpush">
                  <div class="push-frame">
                    <div class="push-frame__header">
                      <div class="push-app">
                        <div class="push-app__icon"><i class="fas fa-handshake"></i></div>
                        <div class="push-app__name">PettyCash</div>
                      </div>
                      <div class="push-time">now</div>
                    </div>
                    <div class="push-content">
                      <div class="push-title">Message from Manager</div>
                      <div class="push-body">{{ broadcastPreview }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeBroadcastDialog">Cancel</button>
            <button
              class="btn btn--send btn--send--broadcast"
              :disabled="!broadcastMessage.trim() || sendingBroadcast"
              @click="sendBroadcast"
            >
              <i v-if="sendingBroadcast" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
              {{ sendingBroadcast ? 'Sending…' : 'Broadcast to ' + selectedPartners.length }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Picker Dialog (NEW) ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplatePickerDialog" class="modal-overlay" @click.self="closeTemplatePickerDialog">
        <div class="modal-card modal-card--picker">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-layer-group"></i>
              </span>
              <div>
                <div class="modal-card__title">Choose a Business Template</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} available</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplatePickerDialog"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
              <small>Create your first template to reuse messages</small>
            </div>
            <div v-else>
              <div class="tpl-search tpl-search--dialog">
                <i class="fas fa-search"></i>
                <input
                  v-model="templatePickerSearch"
                  type="text"
                  placeholder="Search templates by name, subject or content..."
                />
                <button v-if="templatePickerSearch" class="search-clear" @click="templatePickerSearch = ''">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="tpl-picker-list">
                <div
                  v-for="tpl in filteredPickerTemplates"
                  :key="tpl.id"
                  class="tpl-picker-item"
                  @click="selectTemplateFromPicker(tpl)"
                >
                  <div class="tpl-picker-item__top">
                    <span class="tpl-item__name">{{ tpl.name }}</span>
                    <span class="tpl-all-channels-badge">
                      <i class="fas fa-share-alt"></i>
                      All channels
                    </span>
                  </div>
                  <p v-if="tpl.subject" class="tpl-picker-item__subject">
                    <i class="fas fa-heading"></i> {{ tpl.subject }}
                  </p>
                  <p class="tpl-picker-item__body">{{ tpl.message }}</p>
                  <div class="tpl-picker-item__footer">
                    <span class="tpl-manager-item__date">
                      <i class="fas fa-clock"></i>
                      {{ formatDate(tpl.created_at) }}
                    </span>
                    <div class="tpl-compat-icons">
                      <i class="fas fa-envelope" title="Email"></i>
                      <i class="fab fa-whatsapp" title="WhatsApp"></i>
                      <i class="fas fa-bell" title="WebPush"></i>
                      <i class="fas fa-comment-dots" title="Chat"></i>
                    </div>
                  </div>
                </div>
                <div v-if="filteredPickerTemplates.length === 0" class="tpl-empty tpl-empty--big">
                  <i class="fas fa-search"></i>
                  <p>No matching templates</p>
                  <small>Try a different search term</small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplatePickerDialog">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Save Template Dialog ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showSaveTemplateDialog" class="modal-overlay" @click.self="closeSaveTemplate">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-bookmark"></i>
              </span>
              <div>
                <div class="modal-card__title">Save Template</div>
                <div class="modal-card__subtitle">Reuse this message later on any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeSaveTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input
                v-model="templateName"
                type="text"
                class="form-input"
                placeholder="e.g. Welcome new business partner"
                ref="tplNameInput"
                @keyup.enter="confirmSaveTemplate"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Subject
                <span class="form-hint">Optional — auto-filled if email</span>
              </label>
              <input v-model="saveTemplateSubject" type="text" class="form-input" placeholder="Leave empty if not applicable…" />
            </div>
            <div class="form-group">
              <label class="form-label">Message</label>
              <div class="tpl-msg-preview">{{ saveTemplateMessage }}</div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush, Chat).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeSaveTemplate">Cancel</button>
            <button
              class="btn btn--save-confirm"
              :disabled="!templateName.trim() || savingTemplate"
              @click="confirmSaveTemplate"
            >
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Save Template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Manager Modal ════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplateManager" class="modal-overlay" @click.self="closeTemplateManager">
        <div class="modal-card modal-card--manager">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-layer-group"></i>
              </span>
              <div>
                <div class="modal-card__title">Business Message Templates</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} saved</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplateManager"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
            </div>
            <div v-else class="tpl-manager-list">
              <div v-for="tpl in templates" :key="tpl.id" class="tpl-manager-item">
                <div class="tpl-manager-item__top">
                  <span class="tpl-item__name">{{ tpl.name }}</span>
                  <div class="tpl-manager-item__actions">
                    <span class="tpl-all-channels-badge">
                      <i class="fas fa-share-alt"></i>
                      All channels
                    </span>
                    <button class="tpl-del-btn" @click="requestDeleteTemplate(tpl)" title="Delete">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <p v-if="tpl.subject" class="tpl-manager-item__subject">
                  <i class="fas fa-heading"></i> {{ tpl.subject }}
                </p>
                <p class="tpl-manager-item__body">{{ tpl.message }}</p>
                <div class="tpl-manager-item__footer">
                  <span class="tpl-manager-item__date">
                    <i class="fas fa-clock"></i>
                    {{ formatDate(tpl.created_at) }}
                  </span>
                  <div class="tpl-compat-icons">
                    <i class="fas fa-envelope" title="Email"></i>
                    <i class="fab fa-whatsapp" title="WhatsApp"></i>
                    <i class="fas fa-bell" title="WebPush"></i>
                    <i class="fas fa-comment-dots" title="Chat"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplateManager">Close</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Create Template Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showCreateTemplateDialog" class="modal-overlay" @click.self="closeCreateTemplateDialog">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-plus-circle"></i>
              </span>
              <div>
                <div class="modal-card__title">Create new template</div>
                <div class="modal-card__subtitle">Save a reusable message for any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeCreateTemplateDialog">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input
                v-model="createTemplateName"
                type="text"
                class="form-input"
                placeholder="e.g. Welcome new business partner"
                ref="createTplNameInput"
                @keyup.enter="confirmCreateTemplate"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Subject
                <span class="form-hint">Optional — mainly for email</span>
              </label>
              <input
                v-model="createTemplateSubject"
                type="text"
                class="form-input"
                placeholder="Leave empty if not applicable…"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Message
                <span class="form-hint">{name} → partner name · {link} → chat link</span>
              </label>
              <textarea
                v-model="createTemplateMessage"
                class="form-textarea"
                rows="5"
                placeholder="Type your template message…"
              ></textarea>
              <div class="form-actions-row">
                <div class="char-count" :class="{ 'char-count--warn': createTemplateMessage.length > 900 }">
                  {{ createTemplateMessage.length }}/1000
                </div>
              </div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush, Chat).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeCreateTemplateDialog">Cancel</button>
            <button
              class="btn btn--save-confirm"
              :disabled="!createTemplateName.trim() || savingTemplate"
              @click="confirmCreateTemplate"
            >
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Create template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Delete Confirmation Mini Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showDeleteConfirmation" class="modal-overlay" @click.self="cancelDeleteTemplate">
        <div class="modal-card modal-card--confirm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--delete">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <div>
                <div class="modal-card__title">Delete Template</div>
                <div class="modal-card__subtitle">This action cannot be undone</div>
              </div>
            </div>
            <button class="modal-close" @click="cancelDeleteTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <p class="confirm-text">
              Are you sure you want to delete the template<br>
              <strong>"{{ templateToDelete ? templateToDelete.name : '' }}"</strong>?
            </p>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="cancelDeleteTemplate">Cancel</button>
            <button class="btn btn--delete-confirm" @click="confirmDeleteTemplate">
              <i class="fas fa-trash-alt"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

const CHAT_LINK = 'https://myapp.pettycashglobal.com/?open_chat=true';

export default {
  name: 'BusinessSuiteList',

  filters: {
    truncate(val, len) {
      if (!val) return '';
      return val.length > len ? val.slice(0, len) + '…' : val;
    },
  },

  data() {
    return {
      businessSuite: [],
      loading: false,
      searchQuery: '',
      selectedPartners: [],
      headers: [
        { text: 'Username',  value: 'username',         width: '170px' },
        { text: 'Type',      value: 'type',             width: '130px' },
        { text: 'Email',     value: 'email',            width: '220px' },
        { text: 'Phone',     value: 'phone_number',     width: '150px' },
        { text: 'Referrals', value: 'total_affiliates', width: '100px', align: 'center' },
        { text: 'Joined',    value: 'created_at',       width: '120px' },
        { text: 'Actions',   value: 'actions',          sortable: false, width: '180px', align: 'center' },
      ],

      channels: [
        { text: 'Email',    value: 'email',    icon: 'fas fa-envelope' },
        { text: 'WhatsApp', value: 'whatsapp', icon: 'fab fa-whatsapp' },
        { text: 'WebPush',  value: 'webpush',  icon: 'fas fa-bell' },
        { text: 'Chat',     value: 'chat',     icon: 'fas fa-comment-dots' },
      ],
      broadcastChannels: [
        { text: 'Email',   value: 'email',   icon: 'fas fa-envelope' },
        { text: 'WebPush', value: 'webpush', icon: 'fas fa-bell' },
      ],

      showModal: false,
      selectedChannel: 'email',
      selectedPartner: null,
      emailSubject: 'Message from PettyCash',
      messageText: '',
      sending: false,
      previewMessage: '',

      broadcastDialog: false,
      broadcastChannel: 'email',
      broadcastSubject: 'Message from PettyCash',
      broadcastMessage: '',
      sendingBroadcast: false,
      broadcastPreview: '',

      // Template picker dialog
      showTemplatePickerDialog: false,
      templatePickerSource: 'individual',
      templatePickerSearch: '',

      templates: [],
      templatesLoading: false,

      showSaveTemplateDialog: false,
      templateName: '',
      saveTemplateSubject: '',
      saveTemplateMessage: '',
      savingTemplate: false,
      saveTemplateSource: 'individual',

      showTemplateManager: false,

      showCreateTemplateDialog: false,
      createTemplateName: '',
      createTemplateSubject: '',
      createTemplateMessage: '',

      // Suppression mini dialogue
      showDeleteConfirmation: false,
      templateToDelete: null,
    };
  },

  computed: {
    filteredBusinessSuite() {
      let list = this.businessSuite;
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(p => p.username.toLowerCase().includes(q));
      }
      return list;
    },

    filteredPickerTemplates() {
      if (!this.templatePickerSearch.trim()) return this.templates;
      const q = this.templatePickerSearch.toLowerCase().trim();
      return this.templates.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.message && t.message.toLowerCase().includes(q)) ||
        (t.subject && t.subject.toLowerCase().includes(q))
      );
    },

    statsCards() {
      const total = this.businessSuite.length || 1;
      return [
        { key: 'total', label: 'Total Business Suite', value: this.businessSuite.length, icon: 'fas fa-briefcase', color: '#e65100', bg: '#fff3e0', percent: 100 },
        { key: 'affiliates', label: 'Total Referrals', value: this.businessSuite.reduce((acc, p) => acc + (p.total_affiliates || 0), 0), icon: 'fas fa-user-friends', color: '#ef6c00', bg: '#fff8e1', percent: (this.businessSuite.reduce((acc, p) => acc + (p.total_affiliates || 0), 0) / (total || 1)) * 100 },
        { key: 'active', label: 'Active', value: this.businessSuite.length, icon: 'fas fa-check-circle', color: '#2e7d32', bg: '#e8f5e9', percent: 100 },
      ];
    },

    currentTime() {
      return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    },
    currentDate() {
      return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    allFilteredSelected() {
      if (this.filteredBusinessSuite.length === 0) return false;
      return this.selectedPartners.length === this.filteredBusinessSuite.length &&
             this.filteredBusinessSuite.every(fp => this.selectedPartners.some(sp => sp.id === fp.id));
    },
  },

  mounted() {
    this.fetchBusinessSuite();
    this.fetchTemplates();
    document.addEventListener('keydown', this.handleEscape);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
  },

  methods: {
    handleEscape(e) {
      if (e.key !== 'Escape') return;
      if (this.showDeleteConfirmation) { this.cancelDeleteTemplate(); return; }
      if (this.showTemplatePickerDialog) { this.closeTemplatePickerDialog(); return; }
      if (this.showSaveTemplateDialog) { this.closeSaveTemplate(); return; }
      if (this.showTemplateManager)    { this.closeTemplateManager(); return; }
      if (this.showCreateTemplateDialog) { this.closeCreateTemplateDialog(); return; }
      if (this.showModal)              { this.closeModal(); return; }
      if (this.broadcastDialog)        { this.closeBroadcastDialog(); return; }
    },

    toggleSelectAll() {
      if (this.allFilteredSelected) {
        this.selectedPartners = [];
      } else {
        this.selectedPartners = this.filteredBusinessSuite.slice();
      }
    },

    async fetchBusinessSuite() {
      this.loading = true;
      try {
        const response = await axios.get('/api/v1/manager/business-suite/list/');
        this.businessSuite = response.data;
      } catch (error) {
        this.showSnackbar('Error loading business suite', 'red darken-4');
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    viewDetails(id) {
      this.$router.push({ name: 'manager.business_suite.detail', params: { id } });
    },

    channelIcon(channel) {
      return { email: 'fas fa-envelope', whatsapp: 'fab fa-whatsapp', webpush: 'fas fa-bell', chat: 'fas fa-comment-dots' }[channel] || 'fas fa-paper-plane';
    },

    channelLabel(channel) {
      return { email: 'Email', whatsapp: 'WhatsApp', webpush: 'WebPush', chat: 'Chat (in-app)' }[channel] || channel;
    },

    renderPreview(text, partner) {
      if (!text) return 'Your message will appear here…';
      return text.replace(/{name}/g, partner ? partner.username : 'John Doe').replace(/{link}/g, CHAT_LINK);
    },

    openCommunication(partner, channel) {
      this.selectedPartner = partner;
      this.selectedChannel = channel;
      this.messageText = '';
      this.emailSubject = 'Message from PettyCash';
      this.previewMessage = this.renderPreview('', partner);
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.messageText = '';
      this.selectedPartner = null;
    },

    updatePreview() {
      this.previewMessage = this.renderPreview(this.messageText, this.selectedPartner);
    },

    async sendCommunication() {
      if (!this.messageText.trim()) { this.showSnackbar('Please enter a message', 'red darken-4'); return; }
      this.sending = true;
      try {
        const user = this.selectedPartner;
        let endpoint = '', payload = {};

        if (this.selectedChannel === 'email') {
          endpoint = `/api/v1/manager/partners/${user.id}/send-email/`;
          payload = { subject: this.emailSubject, message: this.messageText };
        } else if (this.selectedChannel === 'whatsapp') {
          endpoint = `/api/v1/manager/partners/${user.id}/send-whatsapp/`;
          payload = { message: this.messageText };
        } else if (this.selectedChannel === 'webpush') {
          endpoint = `/api/v1/manager/partners/${user.id}/send-webpush/`;
          payload = { title: 'Message from Manager', message: this.messageText };
        } else if (this.selectedChannel === 'chat') {
          const formData = new FormData();
          formData.append('id_client', user.id);
          formData.append('message', this.messageText);
          formData.append('message_type', 'text');
          await axios.post('/api/v1/manager/save_chat/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.showSnackbar(`Message sent to ${user.username} (in-app)`, 'success');
          this.closeModal();
          return;
        }

        const response = await axios.post(endpoint, payload);
        if (this.selectedChannel === 'whatsapp') {
          if (response.data.whatsapp_url) { window.open(response.data.whatsapp_url, '_blank'); this.closeModal(); }
          else this.showSnackbar('WhatsApp link unavailable', 'red darken-4');
        } else {
          this.showSnackbar(response.data.message || 'Message sent', 'success');
          this.closeModal();
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error sending message', 'red darken-4');
      } finally {
        this.sending = false;
      }
    },

    openBroadcastDialog() {
      if (!this.selectedPartners.length) return;
      this.broadcastMessage = '';
      this.broadcastSubject = 'Message from PettyCash';
      this.broadcastChannel = 'email';
      this.broadcastPreview = this.renderPreview('', this.selectedPartners[0]);
      this.broadcastDialog = true;
    },

    closeBroadcastDialog() {
      this.broadcastDialog = false;
      this.broadcastMessage = '';
    },

    updateBroadcastPreview() {
      this.broadcastPreview = this.renderPreview(this.broadcastMessage, this.selectedPartners[0]);
    },

    async sendBroadcast() {
      if (!this.broadcastMessage.trim()) { this.showSnackbar('Please enter a message', 'red darken-4'); return; }
      this.sendingBroadcast = true;
      try {
        const payload = {
          partner_ids: this.selectedPartners.map(p => p.id),
          channel: this.broadcastChannel,
          subject: this.broadcastSubject,
          message: this.broadcastMessage,
        };
        const response = await axios.post('/api/v1/manager/partners/broadcast/', payload);
        const successCount = (response.data.results || []).filter(r => !r.error).length;
        this.showSnackbar(`Broadcast sent to ${successCount} partner(s)`, 'success');
        this.selectedPartners = [];
        this.closeBroadcastDialog();
      } catch (error) {
        this.showSnackbar('Error sending broadcast', 'red darken-4');
      } finally {
        this.sendingBroadcast = false;
      }
    },

    // ── Templates avec catégorie "business" ──
    async fetchTemplates() {
      this.templatesLoading = true;
      try {
        const response = await axios.get('/api/v1/manager/message-templates/?category=business');
        const data = response.data;
        this.templates = Array.isArray(data) ? data : (data.results || data.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
        this.templates = [];
      } finally {
        this.templatesLoading = false;
      }
    },

    // ── Template Picker Dialog ──
    openTemplatePickerDialog(source) {
      this.templatePickerSource = source;
      this.templatePickerSearch = '';
      this.showTemplatePickerDialog = true;
    },

    closeTemplatePickerDialog() {
      this.showTemplatePickerDialog = false;
      this.templatePickerSearch = '';
    },

    selectTemplateFromPicker(tpl) {
      if (this.templatePickerSource === 'individual') {
        if (tpl.channel && this.channels.some(c => c.value === tpl.channel)) {
          this.selectedChannel = tpl.channel;
        }
        this.messageText = tpl.message;
        if (tpl.subject) this.emailSubject = tpl.subject;
        this.$nextTick(() => this.updatePreview());
      } else {
        if (tpl.channel && this.broadcastChannels.some(c => c.value === tpl.channel)) {
          this.broadcastChannel = tpl.channel;
        }
        this.broadcastMessage = tpl.message;
        if (tpl.subject) this.broadcastSubject = tpl.subject;
        this.$nextTick(() => this.updateBroadcastPreview());
      }
      this.closeTemplatePickerDialog();
    },

    openSaveTemplate(source) {
      this.saveTemplateSource = source;
      if (source === 'individual') {
        this.saveTemplateMessage = this.messageText;
        this.saveTemplateSubject = this.selectedChannel === 'email' ? this.emailSubject : '';
      } else {
        this.saveTemplateMessage = this.broadcastMessage;
        this.saveTemplateSubject = this.broadcastChannel === 'email' ? this.broadcastSubject : '';
      }
      this.templateName = '';
      this.showSaveTemplateDialog = true;
      this.$nextTick(() => { if (this.$refs.tplNameInput) this.$refs.tplNameInput.focus(); });
    },

    closeSaveTemplate() {
      this.showSaveTemplateDialog = false;
      this.templateName = '';
    },

    async confirmSaveTemplate() {
      if (!this.templateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.templateName.trim(),
          subject: this.saveTemplateSubject.trim(),
          message: this.saveTemplateMessage,
          category: 'business',   // obligatoire
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        const newTpl = response.data;
        this.templates.unshift(newTpl);
        this.showSnackbar('Template saved!', 'success');
        this.closeSaveTemplate();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error saving template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    // ── Suppression avec mini dialogue ──
    requestDeleteTemplate(tpl) {
      this.templateToDelete = tpl;
      this.showDeleteConfirmation = true;
    },
    cancelDeleteTemplate() {
      this.showDeleteConfirmation = false;
      this.templateToDelete = null;
    },
    async confirmDeleteTemplate() {
      if (!this.templateToDelete) return;
      const id = this.templateToDelete.id;
      try {
        await axios.delete(`/api/v1/manager/message-templates/${id}/`);
        this.templates = this.templates.filter(t => t.id !== id);
        this.showSnackbar('Template deleted', 'success');
      } catch (error) {
        this.showSnackbar('Error deleting template', 'red darken-4');
      } finally {
        this.cancelDeleteTemplate();
      }
    },

    openTemplateManager()  { this.showTemplateManager = true; },
    closeTemplateManager() { this.showTemplateManager = false; },

    openCreateTemplateDialog() {
      this.createTemplateName = '';
      this.createTemplateSubject = 'Message from PettyCash';
      this.createTemplateMessage = '';
      this.showCreateTemplateDialog = true;
      this.$nextTick(() => { if (this.$refs.createTplNameInput) this.$refs.createTplNameInput.focus(); });
    },
    closeCreateTemplateDialog() { this.showCreateTemplateDialog = false; },

    async confirmCreateTemplate() {
      if (!this.createTemplateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.createTemplateName.trim(),
          subject: this.createTemplateSubject.trim(),
          message: this.createTemplateMessage,
          category: 'business',   // obligatoire
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        const newTpl = response.data;
        this.templates.unshift(newTpl);
        this.showSnackbar('Template created!', 'success');
        this.closeCreateTemplateDialog();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error creating template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    showSnackbar(message, color) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', color);
      this.$store.commit('activateSnackBar', true);
    },
  },
};
</script>



<style scoped>
.dash-root {
  padding: 20px;
  background: #f4f6fb;
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #2d3142;
}

.dash-hero {
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(44, 51, 73, 0.06);
  margin-bottom: 20px;
  overflow: hidden;
}
.dash-hero__bg {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, #e65100 0%, #f57c00 100%);
  opacity: 0.06; pointer-events: none;
}
.dash-hero__inner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 28px 16px; flex-wrap: wrap; gap: 14px;
  position: relative; z-index: 1;
}
.dash-hero__brand { display: flex; align-items: center; gap: 14px; }
.dash-hero__icon {
  width: 48px; height: 48px; background: #fff3e0; color: #e65100;
  border-radius: 14px; display: flex; align-items: center; justify-content: center;
  font-size: 20px; box-shadow: 0 2px 8px rgba(230, 81, 0, 0.15); flex-shrink: 0;
}
.dash-hero__title   { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -0.4px; color: #1a1a2e; line-height: 1.2; }
.dash-hero__subtitle { margin: 3px 0 0; font-size: 13px; color: #7a82a6; font-weight: 500; }
.dash-hero__actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }

.selection-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; color: #e65100; font-weight: 600;
  background: #fff3e0; padding: 6px 14px; border-radius: 20px;
  border: 1px solid #ffe0b2;
}

.btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  border: none; cursor: pointer; transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap; outline: none; flex-shrink: 0;
}
.btn__text { display: inline; }
.btn--primary { background: #e65100; color: #fff; box-shadow: 0 2px 10px rgba(230, 81, 0, 0.25); }
.btn--primary:hover:not(:disabled) { background: #c74500; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(230, 81, 0, 0.3); }
.btn--primary:disabled { opacity: 0.45; cursor: not-allowed; }
.btn--soft { background: #eef0fc; color: #5c6bc0; }
.btn--soft:hover:not(:disabled) { background: #dde1f8; transform: translateY(-1px); }
.btn--soft:disabled { opacity: 0.6; cursor: not-allowed; }
.btn--templates-manager { background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7; }
.btn--templates-manager:hover { background: #e1bee7; }
.btn--ghost { background: transparent; border: 1.5px solid #e9ecf5; color: #6b7a99; }
.btn--ghost:hover { background: #f4f6fb; color: #5c6bc0; border-color: #dde1f8; }
.btn--send { background: #e65100; color: #fff; box-shadow: 0 2px 10px rgba(230, 81, 0, 0.25); }
.btn--send:hover:not(:disabled) { background: #c74500; transform: translateY(-1px); }
.btn--send--whatsapp  { background: #25d366; box-shadow: 0 2px 10px rgba(37,211,102,0.25); }
.btn--send--whatsapp:hover:not(:disabled) { background: #1ebe5a; }
.btn--send--email     { background: #ea4335; box-shadow: 0 2px 10px rgba(234,67,53,0.2); }
.btn--send--email:hover:not(:disabled) { background: #d33b2c; }
.btn--send--webpush   { background: #f4a84a; box-shadow: 0 2px 10px rgba(244,168,74,0.2); }
.btn--send--webpush:hover:not(:disabled) { background: #e0983d; }
.btn--send--broadcast { background: #e65100; }
.btn--send:disabled   { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
.btn--save-confirm {
  background: #6a1b9a; color: #fff;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  border: none; cursor: pointer; transition: all 0.18s ease;
  box-shadow: 0 2px 8px rgba(106, 27, 154, 0.25);
}
.btn--save-confirm:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.btn--save-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

.stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 16px 24px; }
.stat-card {
  background: #ffffff; border-radius: 16px; padding: 18px 20px;
  display: flex; align-items: center; gap: 14px;
  position: relative; overflow: hidden; border: 1px solid #f0f2f8;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(44, 51, 73, 0.07); }
.stat-card__icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
.stat-card__body { flex: 1; min-width: 0; }
.stat-card__val  { display: block; font-size: 22px; font-weight: 800; color: #1a1a2e; line-height: 1.1; letter-spacing: -0.5px; }
.stat-card__lbl  { display: block; font-size: 12px; color: #7a82a6; font-weight: 500; margin-top: 3px; }
.stat-card__bar  { position: absolute; bottom: 0; left: 0; height: 3px; border-radius: 0 0 0 3px; transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1); }

.table-wrap { background: #ffffff; border-radius: 20px; box-shadow: 0 4px 20px rgba(44, 51, 73, 0.06); overflow: hidden; }
.table-toolbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 24px; border-bottom: 1px solid #eef0fc; flex-wrap: wrap; gap: 12px;
}
.search-group {
  display: flex; align-items: center; gap: 10px;
  background: #f9fafc; border: 1.5px solid #e9ecf5; border-radius: 12px;
  padding: 0 14px; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  flex: 1; max-width: 300px;
}
.search-group:focus-within { border-color: #e65100; background: #fff; box-shadow: 0 0 0 3px rgba(230, 81, 0, 0.08); }
.search-group__icon { color: #9aa0ba; font-size: 13px; flex-shrink: 0; }
.search-input { flex: 1; border: none; outline: none; padding: 10px 0; background: transparent; font-size: 13px; color: #2d3142; font-family: inherit; min-width: 0; }
.search-input::placeholder { color: #b0b6cc; }
.search-clear { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: none; background: transparent; color: #9aa0ba; cursor: pointer; font-size: 11px; border-radius: 50%; transition: all 0.15s ease; flex-shrink: 0; }
.search-clear:hover { background: #eef0fc; color: #e65100; }
.table-meta { font-size: 13px; color: #6b7a99; font-weight: 600; display: flex; align-items: center; gap: 7px; flex-shrink: 0; }
.table-meta i { color: #e65100; font-size: 14px; }

.business-table ::v-deep .v-data-table-header { background: #f9fafc !important; }
.business-table ::v-deep th { font-size: 11px !important; font-weight: 700 !important; color: #6b7a99 !important; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef0fc !important; padding: 10px 12px !important; white-space: nowrap; }
.business-table ::v-deep td { font-size: 13px !important; color: #2d3142 !important; border-bottom: 1px solid #f4f6fb !important; padding: 10px 12px !important; vertical-align: middle !important; }
.business-table ::v-deep tr:hover { background: #f8f9fc !important; }
.business-table ::v-deep .v-data-footer { border-top: 1px solid #eef0fc !important; font-size: 12px; color: #6b7a99; padding: 8px 16px; }

.type-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; border: 1px solid transparent; }
.type-badge--business { background: #fff3e0; color: #e65100; border-color: #ffe0b2; }
.phone-cell { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #4a4f6a; }
.phone-cell .fab.fa-whatsapp { color: #25D366; font-size: 13px; }
.phone-cell--missing { color: #d99a3f; font-size: 11px; }
.affiliate-count { display: inline-flex; align-items: center; gap: 5px; background: #eef0fc; color: #5c6bc0; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.date-cell { font-size: 12px; color: #6b7a99; font-weight: 500; }

.action-group { display: flex; gap: 4px; justify-content: center; }
.action-btn { width: 30px; height: 30px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1); background: transparent; color: #9aa0ba; }
.action-btn:hover { transform: translateY(-2px) scale(1.05); }
.action-btn--view:hover     { background: #eef0fc; color: #5c6bc0; }
.action-btn--email:hover    { background: #fce4ec; color: #ea4335; }
.action-btn--whatsapp:hover { background: #e9fbf0; color: #25d366; }
.action-btn--webpush:hover  { background: #fff3e0; color: #f4a84a; }
.action-btn--chat:hover     { background: #eef0fc; color: #5c6bc0; }

.empty-table { text-align: center; padding: 56px 20px; color: #9aa0ba; }
.empty-table__illustration { width: 72px; height: 72px; background: #f4f6fb; border-radius: 20px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.empty-table__illustration i { font-size: 28px; color: #b0b6cc; }
.empty-table p { font-size: 16px; font-weight: 700; color: #6b7a99; margin: 0 0 6px; }
.empty-table small { font-size: 13px; color: #9aa0ba; }

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(30, 34, 58, 0.45);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex; align-items: center; justify-content: center;
  padding: 20px; overflow-y: auto;
}
.modal-card {
  background: #ffffff; border-radius: 20px; width: 100%; max-width: 580px; max-height: 90vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(44, 51, 73, 0.18);
}
.modal-card--broadcast { max-width: 620px; }
.modal-card--save-tpl  { max-width: 460px; }
.modal-card--manager   { max-width: 640px; }
.modal-card--picker    { max-width: 560px; }
.modal-card__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid #eef0fc; gap: 12px; flex-shrink: 0;
}
.modal-card__brand { display: flex; align-items: center; gap: 12px; min-width: 0; }
.modal-card__icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
  background: #eef0fc; color: #5c6bc0;
}
.modal-card__icon--email     { background: #fce4ec; color: #ea4335; }
.modal-card__icon--whatsapp  { background: #e9fbf0; color: #25d366; }
.modal-card__icon--webpush   { background: #fff3e0; color: #f4a84a; }
.modal-card__icon--chat      { background: #eef0fc; color: #5c6bc0; }
.modal-card__icon--broadcast { background: #eef0fc; color: #5c6bc0; }
.modal-card__icon--tpl       { background: #f3e5f5; color: #6a1b9a; }
.modal-card__title    { font-size: 16px; font-weight: 800; color: #1a1a2e; line-height: 1.2; }
.modal-card__subtitle { font-size: 12px; color: #9aa0ba; margin-top: 2px; }
.modal-close { width: 36px; height: 36px; border: none; background: transparent; color: #9aa0ba; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: all 0.15s ease; flex-shrink: 0; }
.modal-close:hover { background: #f4f6fb; color: #5c6bc0; }
.modal-card__body { flex: 1; min-height: 0; overflow-y: auto; padding: 22px 24px; display: flex; flex-direction: column; gap: 18px; }
.modal-card__body::-webkit-scrollbar { width: 5px; }
.modal-card__body::-webkit-scrollbar-thumb { background: #e9ecf5; border-radius: 4px; }
.modal-card__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 24px; border-top: 1px solid #eef0fc; background: #f9fafc; flex-shrink: 0; }

/* SPLIT LAYOUT */
.modal-body--split { display: flex; flex-direction: column; gap: 18px; }
.modal-form-col { display: flex; flex-direction: column; gap: 18px; }
.modal-preview-col { display: flex; flex-direction: column; }
.preview-section--sticky { background: #fafbfc; border-radius: 16px; border: 1.5px solid #e9ecf5; padding: 18px; }

.recipient-bar { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #f4f6fb; border-radius: 10px; border: 1px solid #e9ecf5; }
.recipient-bar__label { font-size: 11px; font-weight: 700; color: #9aa0ba; text-transform: uppercase; letter-spacing: 0.5px; flex-shrink: 0; }
.recipient-chips { display: flex; flex-wrap: wrap; gap: 5px; flex: 1; min-width: 0; }
.recipient-chip { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; color: #e65100; background: #fff3e0; border: 1px solid #ffe0b2; white-space: nowrap; }
.recipient-chip--more { background: #f4f6fb; color: #9aa0ba; border-color: #e9ecf5; }

.channel-picker { display: flex; gap: 8px; flex-wrap: wrap; }
.channel-chip { display: inline-flex; align-items: center; gap: 7px; padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 700; border: 1.5px solid #e9ecf5; background: #f9fafc; color: #6b7a99; cursor: pointer; transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1); outline: none; }
.channel-chip:hover { border-color: #dde1f8; background: #f4f6fb; color: #5c6bc0; transform: translateY(-1px); }
.channel-chip--active { background: #eef0fc; border-color: #5c6bc0; color: #5c6bc0; box-shadow: 0 2px 8px rgba(92, 107, 192, 0.12); transform: translateY(-1px); }
.channel-chip i { font-size: 13px; }

/* Use template button */
.btn-use-template {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; border-radius: 10px;
  background: #f3e5f5; color: #6a1b9a;
  border: 1.5px solid #e1bee7;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.18s ease;
  align-self: flex-start;
}
.btn-use-template:hover { background: #e1bee7; transform: translateY(-1px); }

.tpl-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; border-radius: 10px; background: #6a1b9a; color: #fff; font-size: 11px; font-weight: 700; padding: 0 5px; }

.form-group { display: flex; flex-direction: column; gap: 7px; }
.form-label { font-size: 12px; font-weight: 800; color: #1a1a2e; display: flex; justify-content: space-between; align-items: center; }
.form-hint  { font-size: 11px; color: #9aa0ba; font-weight: 500; }
.form-input,
.form-textarea {
  border: 1.5px solid #e9ecf5; border-radius: 12px; padding: 11px 14px;
  font-family: inherit; font-size: 13px; color: #2d3142;
  background: #f9fafc; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  outline: none; width: 100%; box-sizing: border-box;
}
.form-input:focus,
.form-textarea:focus { border-color: #e65100; background: #ffffff; box-shadow: 0 0 0 3px rgba(230, 81, 0, 0.08); }
.form-textarea { resize: vertical; min-height: 110px; line-height: 1.6; }
.form-input::placeholder,
.form-textarea::placeholder { color: #b0b6cc; }
.form-actions-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.char-count { font-size: 11px; color: #9aa0ba; font-weight: 600; }
.char-count--warn { color: #e53935; font-weight: 800; }
.btn-save-tpl {
  display: inline-flex; align-items: center; gap: 5px;
  background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7;
  border-radius: 16px; padding: 4px 12px; font-size: 11px; font-weight: 600;
  cursor: pointer; transition: all 0.15s ease; white-space: nowrap;
}
.btn-save-tpl:hover { background: #e1bee7; }
.tpl-msg-preview {
  background: #f9fafc; border: 1.5px solid #e9ecf5; border-radius: 10px;
  padding: 10px 12px; font-size: 12px; color: #4a4f6a; line-height: 1.6;
  white-space: pre-wrap; word-break: break-word; max-height: 120px; overflow-y: auto;
}

/* Template picker dialog */
.tpl-search--dialog {
  display: flex; align-items: center; gap: 10px;
  background: #f9fafc; border: 1.5px solid #e9ecf5; border-radius: 12px;
  padding: 0 14px; margin-bottom: 14px;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}
.tpl-search--dialog:focus-within { border-color: #6a1b9a; background: #fff; box-shadow: 0 0 0 3px rgba(106, 27, 154, 0.08); }
.tpl-search--dialog i { color: #9aa0ba; font-size: 13px; flex-shrink: 0; }
.tpl-search--dialog input { flex: 1; border: none; outline: none; padding: 10px 0; background: transparent; font-size: 13px; color: #2d3142; font-family: inherit; min-width: 0; }
.tpl-search--dialog input::placeholder { color: #b0b6cc; }
.tpl-search--dialog .search-clear { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: none; background: transparent; color: #9aa0ba; cursor: pointer; font-size: 11px; border-radius: 50%; transition: all 0.15s ease; flex-shrink: 0; }
.tpl-search--dialog .search-clear:hover { background: #f3e5f5; color: #6a1b9a; }

.tpl-picker-list { display: flex; flex-direction: column; gap: 10px; max-height: 420px; overflow-y: auto; }
.tpl-picker-item {
  border: 1.5px solid #e9ecf5; border-radius: 12px; padding: 14px 16px; background: #fff;
  cursor: pointer; transition: all 0.15s ease;
}
.tpl-picker-item:hover { border-color: #6a1b9a; background: #faf8ff; box-shadow: 0 2px 10px rgba(106, 27, 154, 0.08); transform: translateY(-1px); }
.tpl-picker-item__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.tpl-picker-item__subject { font-size: 12px; color: #6b7a99; font-weight: 500; margin: 0 0 6px; display: flex; align-items: center; gap: 5px; }
.tpl-picker-item__body { font-size: 13px; color: #2d3142; line-height: 1.5; margin: 0 0 10px; white-space: pre-wrap; word-break: break-word; }
.tpl-picker-item__footer { display: flex; align-items: center; justify-content: space-between; }

.tpl-loading--big { padding: 32px; justify-content: center; font-size: 14px; }
.tpl-empty--big { flex-direction: column; align-items: center; justify-content: center; padding: 48px 20px; text-align: center; gap: 10px; }
.tpl-empty--big i { font-size: 32px; opacity: 0.35; }
.tpl-empty--big p { font-size: 14px; font-weight: 600; color: #6b7a99; margin: 0; }
.tpl-empty--big small { font-size: 12px; color: #9aa0ba; }

.tpl-manager-list { display: flex; flex-direction: column; gap: 10px; }
.tpl-manager-item { border: 1.5px solid #e9ecf5; border-radius: 12px; padding: 14px 16px; background: #fff; transition: box-shadow 0.15s ease; }
.tpl-manager-item:hover { box-shadow: 0 2px 10px rgba(44, 51, 73, 0.07); }
.tpl-manager-item__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.tpl-manager-item__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.tpl-info-note { display: flex; align-items: flex-start; gap: 8px; background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7; border-radius: 10px; padding: 10px 12px; font-size: 12px; font-weight: 500; line-height: 1.5; }
.tpl-info-note i { margin-top: 1px; flex-shrink: 0; }
.tpl-manager-item__subject { font-size: 12px; color: #6b7a99; font-weight: 500; margin: 0 0 4px; display: flex; align-items: center; gap: 5px; }
.tpl-manager-item__body { font-size: 13px; color: #2d3142; line-height: 1.5; margin: 0 0 10px; white-space: pre-wrap; word-break: break-word; }
.tpl-manager-item__footer { display: flex; align-items: center; justify-content: space-between; }
.tpl-manager-item__date { font-size: 11px; color: #9aa0ba; display: flex; align-items: center; gap: 5px; }
.tpl-del-btn { width: 26px; height: 26px; border-radius: 8px; border: none; background: transparent; color: #d32f2f; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease; }
.tpl-del-btn:hover { background: #fce4ec; }

.tpl-all-channels-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 9px; border-radius: 10px; font-size: 10px; font-weight: 700; background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7; }
.tpl-compat-icons { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #b0b6cc; }
.tpl-compat-icons .fab.fa-whatsapp    { color: #b2dfdb; }
.tpl-compat-icons .fas.fa-envelope    { color: #f8bbd0; }
.tpl-compat-icons .fas.fa-bell        { color: #ffe0b2; }
.tpl-compat-icons .fas.fa-comment-dots { color: #c5cae9; }

.preview-section { display: flex; flex-direction: column; gap: 12px; }
.preview-header { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 800; color: #6b7a99; text-transform: uppercase; letter-spacing: 0.8px; }
.preview-header i { color: #5c6bc0; font-size: 12px; }
.preview-channel { margin-left: auto; font-size: 10px; font-weight: 800; padding: 3px 10px; border-radius: 20px; background: #eef0fc; color: #5c6bc0; text-transform: none; letter-spacing: 0; }

.preview-whatsapp, .preview-email, .preview-webpush, .preview-chat { display: flex; justify-content: center; }

.wa-phone { width: 100%; max-width: 320px; background: #e5ddd5; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
.wa-phone__header { background: #075e54; color: #fff; padding: 11px 16px; display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; }
.wa-phone__chat { padding: 18px 14px; min-height: 120px; display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.wa-bubble { background: #dcf8c6; border-radius: 12px 2px 12px 12px; padding: 10px 14px; max-width: 88%; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.wa-bubble__text { font-size: 13px; line-height: 1.5; color: #1f2937; white-space: pre-wrap; word-break: break-word; }
.wa-bubble__meta { display: flex; justify-content: flex-end; align-items: center; gap: 4px; margin-top: 4px; }
.wa-bubble__time { font-size: 10px; color: #888; }
.wa-bubble__tick { font-size: 10px; color: #4fc3f7; letter-spacing: -1px; }

.email-frame { width: 100%; max-width: 420px; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 16px rgba(0,0,0,0.06); border: 1px solid #e9ecf5; }
.email-frame__bar { background: #f4f6fb; padding: 9px 14px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #e9ecf5; }
.email-frame__dots { font-size: 8px; letter-spacing: 3px; color: #d1d5db; }
.email-frame__url { flex: 1; text-align: center; font-size: 11px; color: #9aa0ba; background: #fff; padding: 3px 12px; border-radius: 6px; border: 1px solid #e9ecf5; }
.email-frame__body { padding: 18px; }
.email-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid #f0f2f8; }
.email-header__from { display: flex; align-items: center; gap: 10px; }
.email-avatar { width: 34px; height: 34px; border-radius: 50%; background: #5c6bc0; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; }
.email-header__name { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.email-header__addr { font-size: 11px; color: #9aa0ba; }
.email-header__date { font-size: 11px; color: #9aa0ba; font-weight: 500; }
.email-subject { font-size: 14px; font-weight: 800; color: #1a1a2e; margin-bottom: 12px; }
.email-body { font-size: 13px; line-height: 1.6; color: #4a4f6a; white-space: pre-wrap; word-break: break-word; }

.push-frame { width: 100%; max-width: 340px; background: #ffffff; border-radius: 16px; padding: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #e9ecf5; display: flex; flex-direction: column; gap: 10px; }
.push-frame__header { display: flex; justify-content: space-between; align-items: center; }
.push-app { display: flex; align-items: center; gap: 8px; }
.push-app__icon { width: 30px; height: 30px; border-radius: 10px; background: #5c6bc0; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; }
.push-app__name { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.push-time { font-size: 11px; color: #9aa0ba; font-weight: 600; }
.push-content { display: flex; flex-direction: column; gap: 4px; }
.push-title { font-size: 13px; font-weight: 800; color: #1a1a2e; }
.push-body { font-size: 12px; line-height: 1.5; color: #6b7a99; white-space: pre-wrap; word-break: break-word; }

.chat-preview-box { width: 100%; max-width: 420px; background: #f0f2fa; border-radius: 16px; overflow: hidden; border: 1px solid #e9ecf5; }
.chat-preview-header { background: #5c6bc0; color: #fff; padding: 10px 16px; display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; }
.chat-preview-body { padding: 16px; display: flex; justify-content: flex-end; }
.chat-bubble { background: #dcf8c6; padding: 10px 14px; border-radius: 12px 2px 12px 12px; max-width: 80%; font-size: 13px; color: #1f2937; box-shadow: 0 1px 3px rgba(0,0,0,0.06); white-space: pre-wrap; word-break: break-word; }

.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter-active .modal-card, .modal-leave-active .modal-card { transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), opacity 0.25s ease; }
.modal-enter .modal-card, .modal-leave-to .modal-card { transform: scale(0.95) translateY(12px); opacity: 0; }

.pop-enter-active, .pop-leave-active { transition: all 0.2s ease; }
.pop-enter, .pop-leave-to { opacity: 0; transform: scale(0.9); }

.field-slide-enter-active, .field-slide-leave-active { transition: all 0.22s ease; max-height: 80px; opacity: 1; overflow: hidden; }
.field-slide-enter, .field-slide-leave-to { max-height: 0; opacity: 0; }

@media (max-width: 1100px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) {
  .dash-root { padding: 12px; }
  .dash-hero__inner { flex-direction: column; align-items: stretch; }
  .dash-hero__actions { justify-content: flex-start; }
  .stats-grid { grid-template-columns: 1fr; }
  .table-toolbar { flex-direction: column; align-items: stretch; }
  .search-group { max-width: 100%; }
  .modal-overlay { padding: 0; align-items: flex-end; }
  .modal-card { border-radius: 20px 20px 0 0; max-height: 92vh; }
}
@media (max-width: 480px) { .channel-picker { justify-content: center; } }

.btn--select-all {
  background: transparent;
  border: 1.5px solid #e9ecf5;
  color: #6b7a99;
  border-radius: 10px;
  white-space: nowrap;
}
.btn--select-all:hover {
  border-color: #e65100;
  color: #e65100;
  background: #fff3e0;
}

.btn-create-template,
.btn--create-template-manager {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  background: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #e1bee7;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-create-template:hover,
.btn--create-template-manager:hover {
  background: #e1bee7;
}

.btn--create-template-manager {
  margin-right: 8px;
}

.modal-card__head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 768px) {
  .btn--select-all .btn__text {
    display: none;
  }
}

/* DESKTOP SPLIT LAYOUT */
@media (min-width: 900px) {
  .modal-body--split {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 24px;
    align-items: start;
  }
  .modal-preview-col {
    position: sticky;
    top: 0;
    align-self: start;
  }
  .preview-section--sticky {
    position: sticky;
    top: 0;
  }
  .modal-card--comm,
  .modal-card--broadcast {
    max-width: 920px;
  }
}
</style>
